import React, { useCallback, useEffect } from "react";
import { Box, Paper, Tab, Tabs } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useOutletContext, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { usePlatformConsoleOnlinePresence } from "./usePlatformConsoleOnlinePresence";
import PlatformOnlineTabPanel from "./PlatformOnlineTabPanel";
import PlatformWorkspacesOnlineTabPanel from "./PlatformWorkspacesOnlineTabPanel";

const VIEW_OPERATORS = "operators";
const VIEW_WORKSPACES = "workspaces";

/**
 * Presença unificada: operadores online e workspaces online (antes duas abas do console).
 */
const PlatformPresencePage = () => {
  const { t } = useTranslation("app");
  const { registerRefresh, stopRefreshing } = useOutletContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const viewRaw = String(searchParams.get("view") || VIEW_OPERATORS).toLowerCase();
  const view = viewRaw === VIEW_WORKSPACES ? VIEW_WORKSPACES : VIEW_OPERATORS;
  const tabIndex = view === VIEW_WORKSPACES ? 1 : 0;

  const platformPresence = usePlatformConsoleOnlinePresence({
    onLoaded: stopRefreshing,
    onOuterError: stopRefreshing,
  });

  useEffect(() => {
    registerRefresh(() => platformPresence.refreshList());
    return () => registerRefresh(() => stopRefreshing());
  }, [registerRefresh, platformPresence.refreshList, stopRefreshing]);

  const handleTabChange = useCallback(
    (_, v) => {
      const next = v === 1 ? VIEW_WORKSPACES : VIEW_OPERATORS;
      setSearchParams({ view: next }, { replace: true });
    },
    [setSearchParams]
  );

  return (
    <Paper
      variant="outlined"
      sx={(theme) => ({
        bgcolor: alpha(theme.palette.primary.main, 0.03),
        borderColor: "divider",
        overflow: "hidden",
      })}
    >
      <Tabs
        value={tabIndex}
        onChange={handleTabChange}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{
          borderBottom: 1,
          borderColor: "divider",
          bgcolor: "background.paper",
          px: 1,
        }}
        aria-label={t("platformPage.presenceTabsAriaLabel")}
      >
        <Tab label={t("platformPage.tabOnlineOperators")} id="presence-tab-0" aria-controls="presence-panel-0" />
        <Tab label={t("platformPage.tabWorkspacesOnline")} id="presence-tab-1" aria-controls="presence-panel-1" />
      </Tabs>
      <Box
        role="tabpanel"
        id="presence-panel-0"
        aria-labelledby="presence-tab-0"
        hidden={tabIndex !== 0}
        sx={{ p: tabIndex === 0 ? 2.5 : 0, display: tabIndex === 0 ? "block" : "none" }}
      >
        {tabIndex === 0 ? <PlatformOnlineTabPanel presence={platformPresence} /> : null}
      </Box>
      <Box
        role="tabpanel"
        id="presence-panel-1"
        aria-labelledby="presence-tab-1"
        hidden={tabIndex !== 1}
        sx={{ p: tabIndex === 1 ? 2.5 : 0, display: tabIndex === 1 ? "block" : "none" }}
      >
        {tabIndex === 1 ? <PlatformWorkspacesOnlineTabPanel presence={platformPresence} /> : null}
      </Box>
    </Paper>
  );
};

export default PlatformPresencePage;
